"use client";

import { useEffect, useState } from "react";
import { SlideUpIn } from "../animations";
import { getTranslations, Locale } from "@/lib/i18n/config";
import { triggerBrandConfetti } from "@/lib/utils/confetti";

const useRemainingTime = (date: number) => {
  const [remainingTime, setRemainingTime] = useState<number>(() =>
    Math.max(0, date - Date.now())
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(Math.max(0, date - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [date]);

  const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);

  return {
    remainingTime,
    days,
    hours,
    minutes,
    seconds,
  };
};

interface CountdownProps {
  date: number;
  locale?: Locale;
}

export const Countdown: React.FC<CountdownProps> = ({ date, locale }) => {
  const { days, hours, minutes, seconds } = useRemainingTime(date);
  const t = getTranslations(locale || "de");

  const timeSegments = [
    { plural: t.common.days, singular: t.common.day, time: days },
    { plural: t.common.hours, singular: t.common.hour, time: hours },
    { plural: t.common.minutes, singular: t.common.minute, time: minutes },
    { plural: t.common.seconds, singular: t.common.second, time: seconds },
  ];

  return (
    <div
      className="col-span-full flex cursor-pointer select-none flex-row justify-center gap-md sm:gap-xl transition-transform"
      onClick={() => triggerBrandConfetti()}
    >
      {timeSegments.map((segment, index) => (
        <SlideUpIn
          key={`segment-${index}`}
          delay={index * 0.15}
          className={
            "col-span-2 text-center" + (index === 0 ? " col-start-3" : "")
          }
        >
          <TimeSegment
            time={segment.time}
            text={segment.time === 1 ? segment.singular : segment.plural}
          />
        </SlideUpIn>
      ))}
    </div>
  );
};

interface TimeSegmentProps {
  time: number;
  text: string;
  className?: string;
}

const TimeSegment: React.FC<TimeSegmentProps> = ({
  time,
  text,
  className = "",
}) => {
  return (
    <div suppressHydrationWarning className={"width-full " + className}>
      <span className="block headline-3">{String(time)}</span>
      <span className="block">{text}</span>
    </div>
  );
};
